import { Ref, ref } from 'vue';

// Utility type definitions
type ClassListToggle = {
  toggle: (className: string) => ClassListToggle;
  add: (className: string) => ClassListToggle;
  remove: (className: string) => ClassListToggle;
};

export default function useMutatorTools() {
  const timeouts: Ref<number[]> = ref([]);
  const typedChars: Ref<string> = ref('');

  const shuffleArray = <T>(array: T[], makeCopy: boolean = false): T[] => {
    let currentArray = makeCopy ? [...array] : array;
    let m = currentArray.length;
    let tempItem: T;
    let randomIndex: number;

    while (m) {
      randomIndex = Math.floor(Math.random() * m--);
      tempItem = currentArray[m];
      currentArray[m] = currentArray[randomIndex];
      currentArray[randomIndex] = tempItem;
    }

    return currentArray;
  };

  const roundToNearestTenth = (num: number): number => {
    return Math.round(num * 10) / 10;
  };

  const formatText = (
    message: string,
    values: Record<string, unknown>,
    useHTML: boolean = true
  ): string => {
    // Replace all {{variable}} occurrences with their values
    let formattedText = message.replace(/\{\{([^}]+)\}\}/g, (match, key) => {
      // Handle nested object properties (e.g., "it.count")
      const props = key.trim().split('.');
      let value = values;

      for (const prop of props) {
        value = value?.[prop];
        if (value === undefined) {
          // If value not found, return original placeholder
          return match;
        }
      }

      // Convert value to string, handling null/undefined
      return String(value ?? '');
    });

    // Handle HTML wrapping if needed
    if (useHTML && !formattedText.startsWith('<p>')) {
      formattedText = `<p>${formattedText}</p>`;
    }

    return formattedText;
  };
  const getTodaysDate = (): string => {
    const d = new Date();
    let date = d.getDate();
    let month = d.getMonth() + 1;

    return month + '/' + date;
  };

  const isEmpty = (obj: Record<string, unknown> | unknown[]): boolean => {
    if (!obj) return true;
    return (
      (obj.constructor === Object || obj.constructor === Array) &&
      !Object.entries(obj).length
    );
  };

  const intersection = <T>(arrays: T[][]): T[] => {
    return arrays.reduce((a, b) => a.filter((c) => b.includes(c)));
  };

  const getSample = <T>(arr: T[], n: number): T[] => {
    let result = new Array<T>(n),
      len = arr.length,
      taken = new Array(len);

    if (n > len)
      throw new RangeError('getRandom: more elements taken than available');
    while (n--) {
      let x = Math.floor(Math.random() * len);
      result[n] = arr[x in taken ? taken[x] : x];
      taken[x] = --len in taken ? taken[len] : len;
    }
    return result;
  };

  const capitalize = (string: string): string => {
    return string[0].toUpperCase() + string.slice(1);
  };

  const classList = (elt: Element): ClassListToggle => {
    const list = elt.classList;

    return {
      toggle: function (c: string) {
        list.toggle(c);
        return this;
      },
      add: function (c: string) {
        list.add(c);
        return this;
      },
      remove: function (c: string) {
        list.remove(c);
        return this;
      },
    };
  };

  const createUniqueKey = (): string => {
    return Math.random().toString(36).substring(2, 32);
  };

  const flattenObject = (
    obj: Record<string, unknown>,
    parentKey: string = ''
  ): Record<string, unknown> => {
    let flattenedObject: Record<string, unknown> = {};

    for (let [key, value] of Object.entries(obj)) {
      const newKey = parentKey ? `${parentKey}.${key}` : key;

      if (value !== null && typeof value === 'object') {
        flattenedObject = {
          ...flattenedObject,
          ...flattenObject(value as Record<string, unknown>, newKey),
        };
      } else {
        flattenedObject[newKey] = value;
      }
    }

    return flattenedObject;
  };

  const characters = (typedText: string, delay: number = 50): void => {
    timeouts.value.forEach((timeout) => clearTimeout(timeout));
    timeouts.value = [];

    const chars = typedText.split('');
    typedChars.value = '';

    chars.forEach((char, index) => {
      const typedChar = window.setTimeout(() => {
        typedChars.value += char;

        if (index === chars.length - 1) {
          clearTimeout(typedChar);
        }
      }, index * delay);

      timeouts.value.push(typedChar);
    });
  };

  return {
    timeouts,
    typedChars,
    shuffleArray,
    roundToNearestTenth,
    formatText,
    getTodaysDate,
    isEmpty,
    intersection,
    getSample,
    capitalize,
    classList,
    createUniqueKey,
    flattenObject,
    characters,
  };
}
